/**
 * Per-arche "focus mode" chrome state — whether an arche's upper bars (the
 * platform Header rendered by ArcheLayout AND the arche's own ArcheHeader) are
 * hidden so the arche can run chrome-free.
 *
 * State is keyed by the canonical arche id (`arche_*`), so hiding chrome in one
 * arche (e.g. email) never affects another (e.g. todo-lists); each restores its
 * own state on return and across reloads (localStorage-backed).
 *
 * Safety: this is NOT browser fullscreen — no Fullscreen API. The context ships
 * a working in-memory DEFAULT value, so `useChrome()` outside a provider never
 * throws (existing arche component tests render ArcheHeader without wrapping the
 * provider). All localStorage access is guarded — malformed JSON, unavailable
 * storage (SSR/test/private mode), or a write failure all fall back to an
 * in-memory map and must never break app startup.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'fias-chrome-hidden-by-arche';

type HiddenMap = Record<string, boolean>;

export interface ChromeContextValue {
  /** True if this arche currently has its upper chrome hidden. */
  isHeadersHidden: (archeId: string | undefined) => boolean;
  /** Flip the hidden state for one arche. */
  toggleHeaders: (archeId: string) => void;
  /** Force-show the chrome for one arche (the restore affordance). */
  showHeaders: (archeId: string) => void;
}

/** Read the persisted map, tolerating malformed/absent/unavailable storage. */
function readStored(): HiddenMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: HiddenMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (v === true) out[k] = true;
    }
    return out;
  } catch {
    return {};
  }
}

/** Persist only the `true` entries; never throw if storage is unavailable. */
function writeStored(map: HiddenMap): void {
  try {
    const trimmed: HiddenMap = {};
    for (const [k, v] of Object.entries(map)) if (v) trimmed[k] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // storage unavailable / quota — state still lives in memory
  }
}

// In-memory no-op default → useChrome() without a provider is safe (returns
// "not hidden" and ignores mutations) rather than throwing — this keeps the many
// arche component tests that render ArcheHeader without a provider working. A
// MUTATION with no provider is a wiring bug (the provider is app-level), so warn
// in dev rather than fail silently.
function warnNoProvider(fn: string): void {
  if (import.meta.env.DEV) {
    console.warn(
      `[ChromeContext] ${fn}() called with no <ChromeProvider> mounted — no-op. ` +
        `It is mounted app-wide in App.tsx; this indicates a missing provider.`,
    );
  }
}

const defaultValue: ChromeContextValue = {
  isHeadersHidden: () => false,
  toggleHeaders: () => warnNoProvider('toggleHeaders'),
  showHeaders: () => warnNoProvider('showHeaders'),
};

const ChromeContext = createContext<ChromeContextValue>(defaultValue);

export function ChromeProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState<HiddenMap>(() => readStored());

  // Persist on change (effect, not inside the updater — pure updaters + a single
  // write per change, safe under StrictMode double-invocation).
  useEffect(() => {
    writeStored(hidden);
  }, [hidden]);

  const isHeadersHidden = useCallback(
    (archeId: string | undefined) => (archeId ? hidden[archeId] === true : false),
    [hidden],
  );

  const toggleHeaders = useCallback((archeId: string) => {
    setHidden((prev) => {
      const next = { ...prev };
      if (next[archeId]) delete next[archeId];
      else next[archeId] = true;
      return next;
    });
  }, []);

  const showHeaders = useCallback((archeId: string) => {
    setHidden((prev) => {
      if (!prev[archeId]) return prev;
      const next = { ...prev };
      delete next[archeId];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ isHeadersHidden, toggleHeaders, showHeaders }),
    [isHeadersHidden, toggleHeaders, showHeaders],
  );

  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
}

export function useChrome(): ChromeContextValue {
  return useContext(ChromeContext);
}
